import React from "react";
import JSZip from "jszip";
import toast from "react-hot-toast";
import { Document, Page, StyleSheet, Text, View, pdf } from "@react-pdf/renderer";
import { fetchAttachment } from "../components/Brief/downloadAttachment";
import {
    Brief,
    BriefPdpUrl,
    briefAssetFileName,
    briefDisclaimerRows,
    briefHasRmnAssets,
    briefPdpLinks,
} from "../components/Brief/constants";
import {
    TdcPdfHeader,
    TdcPdfSectionHeading,
    downloadPdfBlob,
    pdfExportedStamp,
    pdfFileName,
    pluralCount,
    tdcPdfStyles,
} from "./pdf/tdcPdfTheme";

export type { Brief };

const styles = StyleSheet.create({
    page: tdcPdfStyles.page,
    field: tdcPdfStyles.entry,
    label: tdcPdfStyles.entryLabel,
    value: tdcPdfStyles.entryValue,
});

interface FieldConfig {
    label: string;
    value: string;
}

interface BriefPDFProps {
    brief: Brief;
    projectTitle?: string;
    professionText?: string;
}

const formatFieldValue = (value?: string | number): string => {
    if (value === undefined || value === null || value === "") return "-";
    return String(value);
};

const describeLink = (
    url: string,
    pdpUrl?: BriefPdpUrl | null,
    productTitle?: string | null
): string => {
    const notes = [
        pdpUrl?.retailer ? `${pdpUrl.retailer} listing` : null,
        pdpUrl && !pdpUrl.active ? "not scored yet" : null,
    ].filter(Boolean);
    const suffix = notes.length > 0 ? ` (${notes.join(", ")})` : "";
    return productTitle ? `${productTitle}: ${url}${suffix}` : `${url}${suffix}`;
};

// One line per product on the project; older briefs only carry the single url.
const pdpUrlText = (brief: Brief): string => {
    const filled = briefPdpLinks(brief);
    if (filled.length === 0) return formatFieldValue("");
    return filled
        .map((link) =>
            describeLink(
                link.url,
                link.pdpUrl,
                filled.length > 1 ? link.product_title : null
            )
        )
        .join("\n");
};

const expertTypeText = (brief: Brief): string => {
    const type = (brief as any).expert_type;
    if (type === "industry") return "Industry Expert, a credentialed professional in the field";
    if (type === "use_case") {
        return "Use-Case Expert, a credible practitioner who uses it in real life";
    }
    return "Either / No Preference";
};

const professionsText = (brief: Brief, professionText?: string): string => {
    if (professionText) return professionText;
    if (brief.professions && brief.professions.length > 0) {
        return `${brief.professions.length} profession(s) selected`;
    }
    return "-";
};

const fileText = (url?: string | null): string => (url ? briefAssetFileName(url) : "-");

// TDC-1722 brand assets + product disclaimers. Files print by name; the files
// themselves ride in the download zip next to the attachments.
// '' = the supplier's brief_* set, 'rmn_' = the retail media network's.
const assetSetFields = (brief: Brief, prefix: string): FieldConfig[] => {
    const value = (key: string): string | undefined => (brief as any)[`${prefix}${key}`] || undefined;
    return [
        { label: "Brand tagline", value: formatFieldValue(value("brief_tagline")) },
        { label: "Logo for videos", value: fileText(value("brief_logo")) },
        { label: "Brand colors", value: formatFieldValue(value("brief_colors")) },
        { label: "Brand colors file", value: fileText(value("brief_colors_file")) },
        { label: "Brand font", value: formatFieldValue(value("brief_font")) },
        { label: "Brand font file", value: fileText(value("brief_font_file")) },
    ];
};

const brandAssetFields = (brief: Brief): FieldConfig[] => {
    const disclaimers = briefDisclaimerRows(brief);
    return [
        ...assetSetFields(brief, ""),
        ...(disclaimers.length === 0
            ? [{ label: "Disclaimers", value: "-" }]
            : disclaimers.map((row) => ({
                label: row.title ? `Disclaimers — ${row.title}` : "Disclaimers",
                value: row.text,
            }))),
    ];
};

const Section: React.FC<{ label: string; fields: FieldConfig[] }> = ({ label, fields }) => (
    <>
        <TdcPdfSectionHeading label={label} count={pluralCount(fields.length, "field")} />
        {fields.map((field) => (
            <View key={field.label} style={styles.field} wrap={false}>
                <Text style={styles.label}>{field.label}</Text>
                <Text style={styles.value}>{field.value}</Text>
            </View>
        ))}
    </>
);

const BriefPDF: React.FC<BriefPDFProps> = ({ brief, projectTitle, professionText }) => {
    const expertQuestions: FieldConfig[] = [
        { label: "Gender", value: formatFieldValue(brief.expert_gender) },
        {
            label: "Age Range",
            value: `${brief.expert_age_min || 0} - ${brief.expert_age_max || 0}`,
        },
        { label: "Expert Type", value: expertTypeText(brief) },
        { label: "Professions/Expertise", value: professionsText(brief, professionText) },
        { label: "Interests", value: formatFieldValue(brief.expert_interests) },
        { label: "Other Requirements", value: formatFieldValue(brief.other) },
        {
            label: "List any filming appearance requests for selected Expert",
            value: formatFieldValue(brief.special_requests),
        },
    ];

    const intakeForm: FieldConfig[] = [
        { label: "If the product is live, please link its PDP", value: pdpUrlText(brief) },
        {
            label: "List the product name as you would like it to be first introduced in the video, and include its phonetic pronunciation",
            value: formatFieldValue(brief.name),
        },
        {
            label: "List 2-3 shortened product names that are approved to be used throughout the rest of the video",
            value: formatFieldValue(brief.short_name),
        },
        {
            label: "List the 3-5 most important product features and benefits you'd like to be highlighted in the video in order of priority/importance. Simple language is encouraged",
            value: formatFieldValue(brief.description),
        },
        {
            label: "List any requests for the filming location/setting",
            value: formatFieldValue(brief.location_request),
        },
        {
            label: "Any other specific brand requests",
            value: formatFieldValue(brief.brand_requests),
        },
    ];

    const productName = brief.name && String(brief.name).trim() ? String(brief.name) : undefined;

    return (
        <Document>
            <Page size="A4" style={styles.page}>
                <TdcPdfHeader
                    title={projectTitle || "Project Brief"}
                    subtitle={productName}
                    meta={pdfExportedStamp("PROJECT BRIEF")}
                />
                <Section label="Expert Questions" fields={expertQuestions} />
                <Section label="Intake Form" fields={intakeForm} />
                <Section label="Brand & Product Assets" fields={brandAssetFields(brief)} />
                {briefHasRmnAssets(brief) && (
                    <Section label="Retailer Assets" fields={assetSetFields(brief, "rmn_")} />
                )}
            </Page>
        </Document>
    );
};

export const generateBriefPDF = async (
    brief: Brief,
    projectTitle?: string,
    professionText?: string,
): Promise<Blob> =>
    pdf(
        <BriefPDF brief={brief} projectTitle={projectTitle} professionText={professionText} />,
    ).toBlob();

export const downloadBriefPDF = async (
    brief: Brief,
    projectTitle?: string,
    professionText?: string,
): Promise<void> => {
    const blob = await generateBriefPDF(brief, projectTitle, professionText);
    const pdfName = pdfFileName(projectTitle, "brief", "project");
    const attachments = [
        ...(brief.attachments || [])
            .filter((a) => !!a.location)
            .map((a) => ({ name: a.name, location: a.location as string })),
        ...[
            brief.brief_logo,
            brief.brief_colors_file,
            brief.brief_font_file,
            brief.rmn_brief_logo,
            brief.rmn_brief_colors_file,
            brief.rmn_brief_font_file,
        ]
            .filter((url): url is string => !!url)
            .map((url) => ({ name: briefAssetFileName(url), location: url })),
    ];

    if (attachments.length === 0) {
        downloadPdfBlob(blob, pdfName);
        return;
    }

    const zip = new JSZip();
    zip.file(pdfName, blob);
    const folder = zip.folder("attachments");
    const fetched = await Promise.all(
        attachments.map(async (attachment) => {
            try {
                const response = await fetchAttachment(attachment.location);
                return { name: attachment.name, body: await response.blob() };
            } catch {
                return null;
            }
        }),
    );
    fetched.forEach((entry, index) => {
        if (!entry) return;
        const unique = folder?.file(entry.name)
            ? `${index + 1}-${entry.name}`
            : entry.name;
        folder?.file(unique, entry.body);
    });

    const missing = fetched.filter((entry) => !entry).length;
    if (missing > 0) {
        toast.error(
            `${missing} attachment${missing > 1 ? "s" : ""} could not be added to the download.`,
        );
    }

    const archive = await zip.generateAsync({ type: "blob" });
    downloadPdfBlob(archive, `${pdfName.replace(/\.pdf$/i, "")}.zip`);
};
